// EventVenuePage.jsx (React 19 + MUI)
// ✅ 변경사항: 최소 인원 범위를 드래그(react-range) 대신 "숫자 입력"으로 변경
// - 숫자 입력 시 즉시(실시간) 필터링
// - 키워드/지역 변경 시 자동 검색(디바운스)
// - 시군구 클릭 시 즉시 검색
// - 전체선택(전국) / 전체선택(현재 시/도) 분리

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

// MUI
import {
    Alert,
    Backdrop,
    Box,
    CircularProgress,
    Container,
    Divider,
    IconButton,
    Paper,
    Stack,
    TextField,
    Typography,
    Chip,
    Button,
    InputAdornment,
} from "@mui/material";
import { notify } from "../../../util/toast";
import LoadingDots from "../../../component/common/LoadingDots";
import ActionButton from "../../../component/common/ActionButton";

import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import SelectAllRoundedIcon from "@mui/icons-material/SelectAllRounded";
import RemoveDoneRoundedIcon from "@mui/icons-material/RemoveDoneRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded"; // 전국
import DomainRoundedIcon from "@mui/icons-material/DomainRounded"; // 시/도
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import { ds } from "../../../styles/designSystem";
import { eventVenueApi } from "./api/eventVenueApi";
import { getApiErrorMessage } from "../../../util/apiFeedback";

const MIN = 0;
const MAX = 100000; // 필요하면 더 키우세요

export default function EventVenuePage() {
    const [groupedRegions, setGroupedRegions] = useState({});
    const [activeSido, setActiveSido] = useState("");
    const [selectedSigungu, setSelectedSigungu] = useState([]);

    // ✅ 숫자 입력용: 문자열 상태(타이핑 중 1, 10, 100 등을 자연스럽게 처리)
    const [minCapacityInput, setMinCapacityInput] = useState("0");
    // ✅ 실제 필터링에 쓰는 숫자 상태
    const [minCapacity, setMinCapacity] = useState(0);

    const [keyword, setKeyword] = useState("");

    // ✅ 서버 검색 결과(원본)
    const [venues, setVenues] = useState([]);
    // ✅ 화면 표시용(실시간 필터 적용)
    const [visibleVenues, setVisibleVenues] = useState([]);

    const [searchTriggered, setSearchTriggered] = useState(false);

    const [loadingRegions, setLoadingRegions] = useState(false);
    const [loadingVenues, setLoadingVenues] = useState(false);


    // ✅ 중복 호출 방지용
    const lastQueryRef = useRef("");

    // ---------------------------
    // 1) 지역 목록 로딩
    // ---------------------------
    useEffect(() => {
        (async () => {
            try {
                setLoadingRegions(true);
                const regions = await eventVenueApi.getRegions();
                const arr = Array.isArray(regions) ? regions : [];

                const grouped = arr.reduce((acc, cur) => {
                    const { sido, venueCount } = cur;
                    if (!acc[sido]) {
                        acc[sido] = { sido, totalVenueCount: 0, sigunguList: [] };
                    }
                    acc[sido].totalVenueCount += Number(venueCount || 0);
                    acc[sido].sigunguList.push(cur);
                    return acc;
                }, {});

                setGroupedRegions(grouped);

                const firstSidoKey = Object.keys(grouped)[0];
                if (firstSidoKey) setActiveSido(firstSidoKey);
            } catch (e) {
                console.error(e);
                notify(getApiErrorMessage(e, "지역 목록을 불러오지 못했습니다."), "error");
            } finally {
                setLoadingRegions(false);
            }
        })();
    }, []);

    const sidoKeys = useMemo(() => Object.keys(groupedRegions || {}), [groupedRegions]);
    const activeSidoObj = useMemo(
        () => (activeSido ? groupedRegions?.[activeSido] ?? null : null),
        [activeSido, groupedRegions]
    );

    // ---------------------------
    // 2) 서버 검색
    // ---------------------------
    const buildSearchParams = useCallback(() => {
        const parsedSigungus = selectedSigungu.map((key) => key.split("-")[1]);
        const parsedSidos = selectedSigungu.map((key) => key.split("-")[0]);

        return {
            sidos: Array.from(new Set(parsedSidos)),
            sigungus: parsedSigungus,
            // ✅ minCapacity는 "실시간 필터"로만 쓰고 서버엔 0으로(원하면 서버에도 넣을 수 있음)
            minCapacity: 0,
            keyword: keyword.trim(),
        };
    }, [keyword, selectedSigungu]);

    const fetchVenues = useCallback(
        async (reason = "auto") => {
            const params = buildSearchParams();
            const queryKey = JSON.stringify(params);

            if (lastQueryRef.current === queryKey && reason !== "force") return;
            lastQueryRef.current = queryKey;

            setSearchTriggered(true);

            try {
                setLoadingVenues(true);
                setVenues([]);
                setVisibleVenues([]);

                const data = await eventVenueApi.search(params);
                const list = Array.isArray(data) ? data : [];
                setVenues(list);
            } catch (e) {
                console.error(e);
                notify(getApiErrorMessage(e, "검색에 실패했습니다."), "error");
            } finally {
                setLoadingVenues(false);
            }
        },
        [buildSearchParams]
    );

    // ✅ 키워드/지역 변경 시 자동 검색(디바운스)
    useEffect(() => {
        const t = setTimeout(() => {
            if (keyword.trim() || selectedSigungu.length > 0) {
                fetchVenues("auto");
            }
        }, 350);
        return () => clearTimeout(t);
    }, [keyword, selectedSigungu.length, fetchVenues]);

    // ---------------------------
    // 3) 최소 인원 “실시간 필터링”
    // ---------------------------
    useEffect(() => {
        const filtered = (venues || []).filter((v) => {
            const min = Number(v?.minCapacity ?? 0);
            const max = Number(v?.maxCapacity ?? 0);
            if (minCapacity <= 0) return true;
            return min <= minCapacity && max >= minCapacity;
        });
        setVisibleVenues(filtered);
    }, [venues, minCapacity]);

    // ---------------------------
    // 4) 지역 선택: 전체선택(전국) / 전체선택(현재 시/도) / 개별 토글
    // ---------------------------
    const toggleSigungu = useCallback(
        (sido, sigungu) => {
            const key = `${sido}-${sigungu}`;
            setSelectedSigungu((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

            // ✅ 시군구 클릭 시 즉시 검색
            setTimeout(() => fetchVenues("force"), 0);
        },
        [fetchVenues]
    );

    const toggleAllInActiveSido = useCallback(() => {
        if (!activeSidoObj) return;
        const list = Array.isArray(activeSidoObj?.sigunguList) ? activeSidoObj.sigunguList : [];
        const keys = list.map((item) => `${item.sido}-${item.sigungu}`);

        setSelectedSigungu((prev) => {
            const allSelected = keys.every((k) => prev.includes(k));
            return allSelected ? prev.filter((k) => !keys.includes(k)) : Array.from(new Set([...prev, ...keys]));
        });

        setTimeout(() => fetchVenues("force"), 0);
    }, [activeSidoObj, fetchVenues]);

    const toggleAllNationwide = useCallback(() => {
        const allKeys = [];
        Object.keys(groupedRegions || {}).forEach((sidoKey) => {
            const list = groupedRegions?.[sidoKey]?.sigunguList || [];
            list.forEach((item) => allKeys.push(`${item.sido}-${item.sigungu}`));
        });

        setSelectedSigungu((prev) => {
            const allSelected = allKeys.length > 0 && allKeys.every((k) => prev.includes(k));
            return allSelected ? [] : Array.from(new Set(allKeys));
        });

        setTimeout(() => fetchVenues("force"), 0);
    }, [groupedRegions, fetchVenues]);

    const isAllSelectedNationwide = useMemo(() => {
        const allKeys = [];
        Object.keys(groupedRegions || {}).forEach((sidoKey) => {
            const list = groupedRegions?.[sidoKey]?.sigunguList || [];
            list.forEach((item) => allKeys.push(`${item.sido}-${item.sigungu}`));
        });
        if (allKeys.length === 0) return false;
        return allKeys.every((k) => selectedSigungu.includes(k));
    }, [groupedRegions, selectedSigungu]);

    const isAllSelectedInActiveSido = useMemo(() => {
        const list = activeSidoObj?.sigunguList || [];
        if (list.length === 0) return false;
        return list.every((item) => selectedSigungu.includes(`${item.sido}-${item.sigungu}`));
    }, [activeSidoObj, selectedSigungu]);

    // ---------------------------
    // 5) 초기화
    // ---------------------------
    const resetOptions = useCallback(() => {
        setSelectedSigungu([]);
        setVenues([]);
        setVisibleVenues([]);
        setKeyword("");

        setMinCapacityInput("0");
        setMinCapacity(0);

        setSearchTriggered(false);
        lastQueryRef.current = "";
        notify("초기화되었습니다.", "success");
    }, []);

    // ---------------------------
    // 6) 숫자 입력 핸들링 (minCapacityInput -> minCapacity)
    // ---------------------------
    const clamp = useCallback((n) => Math.min(MAX, Math.max(MIN, n)), []);

    const commitMinCapacity = useCallback(
        (raw) => {
            // 숫자만 추출 (예: "1,000" 입력해도 OK)
            const onlyNum = String(raw ?? "").replace(/[^\d]/g, "");
            const parsed = onlyNum === "" ? 0 : Number(onlyNum);
            const next = clamp(parsed);

            setMinCapacity(next);
            setMinCapacityInput(String(next));
        },
        [clamp]
    );

    const handleMinCapacityChange = useCallback(
        (e) => {
            const v = e.target.value;
            // 타이핑 중엔 문자열 그대로 유지(빈값도 허용)
            const cleaned = String(v).replace(/[^\d]/g, "");
            setMinCapacityInput(cleaned);

            // 실시간 필터: 입력 즉시 숫자로 반영
            const parsed = cleaned === "" ? 0 : Number(cleaned);
            setMinCapacity(clamp(parsed));
        },
        [clamp]
    );

    const handleMinCapacityBlur = useCallback(() => {
        commitMinCapacity(minCapacityInput);
    }, [commitMinCapacity, minCapacityInput]);

    const handleMinCapacityKeyDown = useCallback(
        (e) => {
            if (e.key === "Enter") {
                e.preventDefault();
                commitMinCapacity(minCapacityInput);
            }
        },
        [commitMinCapacity, minCapacityInput]
    );

    // ---------------------------
    // 7) UI helpers
    // ---------------------------
    const selectedChips = useMemo(() => {
        return selectedSigungu.map((key) => {
            const [sido, sigungu] = key.split("-");
            return { key, label: sigungu ? `${sido} · ${sigungu}` : key };
        });
    }, [selectedSigungu]);

    const RoundActionButton = ({ icon, label, onClick, disabled, variant = "filled" }) => (
        <Stack spacing={0.75} alignItems="center">
            <IconButton
                onClick={onClick}
                disabled={disabled}
                sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "999px",
                    ...(variant === "filled"
                        ? { bgcolor: ds.colors.primary, color: "#fff", "&:hover": { bgcolor: ds.colors.primaryDark } }
                        : {
                            bgcolor: ds.colors.surface,
                            color: ds.colors.primary,
                            border: `1px solid ${ds.colors.primaryBorder}`,
                            "&:hover": { bgcolor: ds.colors.primaryBg },
                        }),
                    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
                }}
            >
                {icon}
            </IconButton>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary" }}>{label}</Typography>
        </Stack>
    );

    return (
        <Box sx={ds.components.external.page}>
            <Container maxWidth="xl" sx={{ py: 3 }}>

                <Box sx={{ mb: 3 }}>
                    <Typography sx={ds.typography.pageTitle}>행사장 검색</Typography>
                    <Typography sx={{ ...ds.typography.pageSubtitle, mt: 0.4 }}>
                        지역(시·군·구) + 최소 인원 + 키워드로 행사장을 검색합니다
                    </Typography>
                </Box>

                <Paper sx={{ ...ds.components.external.card, p: 2.5 }}>
                    <Stack spacing={2.25}>
                        {/* 헤더 */}

                        {/* ✅ 최소 인원: 숫자 입력 */}
                        <Stack
                            direction={{ xs: "column", md: "row" }}
                            spacing={1.5}
                            alignItems={{ xs: "stretch", md: "center" }}
                        >


                            <TextField
                                value={minCapacityInput}
                                onChange={handleMinCapacityChange}
                                onBlur={handleMinCapacityBlur}
                                onKeyDown={handleMinCapacityKeyDown}
                                placeholder="예: 200"
                                size="small"
                                inputMode="numeric"
                                fullWidth
                                sx={{ ...ds.components.external.inputSoft, maxWidth: { xs: "100%", md: 280 } }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <PeopleAltRoundedIcon sx={{ opacity: 0.6 }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: <InputAdornment position="end">명 이상</InputAdornment>,
                                }}
                                helperText={`최소 ${minCapacity.toLocaleString("ko-KR")}명 (입력 즉시 결과가 실시간으로 필터링됩니다)`}
                                FormHelperTextProps={{ sx: { fontWeight: 600, m: 0, mt: 0.75 } }}
                            />




                        </Stack>

                        {/* 행사장 이름 + 둥근 버튼 */}
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <TextField
                                value={keyword}
                                onChange={(e) => setKeyword(e.target.value)}
                                placeholder="행사장 이름 검색"
                                size="small"
                                fullWidth
                                sx={ds.components.external.inputSoft}
                            />

                            <RoundActionButton
                                icon={<SearchRoundedIcon />}
                                label="검색"
                                onClick={() => fetchVenues("force")}
                                disabled={loadingVenues || loadingRegions}
                                variant="filled"
                            />
                            <RoundActionButton
                                icon={<RestartAltRoundedIcon />}
                                label="초기화"
                                onClick={resetOptions}
                                disabled={loadingVenues || loadingRegions}
                                variant="outlined"
                            />
                        </Stack>

                        {/* ✅ 전체 선택 버튼 2종(전국 / 현재 시·도) */}
                        <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ xs: "stretch", md: "center" }}>
                            <ActionButton
                                variant={isAllSelectedNationwide ? "primary" : "secondary"}
                                startIcon={isAllSelectedNationwide ? <RemoveDoneRoundedIcon sx={{ fontSize: 16 }} /> : <PublicRoundedIcon sx={{ fontSize: 16 }} />}
                                onClick={toggleAllNationwide}
                                disabled={loadingRegions || loadingVenues || sidoKeys.length === 0}
                                label={isAllSelectedNationwide ? "전체 해제(전국)" : "전체 선택(전국)"}
                                sx={{ px: 2.25, py: 1 }}
                            />

                            <ActionButton
                                variant={isAllSelectedInActiveSido ? "primary" : "secondary"}
                                startIcon={isAllSelectedInActiveSido ? <RemoveDoneRoundedIcon sx={{ fontSize: 16 }} /> : <DomainRoundedIcon sx={{ fontSize: 16 }} />}
                                onClick={toggleAllInActiveSido}
                                disabled={loadingRegions || loadingVenues || !activeSidoObj}
                                label={isAllSelectedInActiveSido ? `전체 해제(${activeSido})` : `전체 선택(${activeSido || "시/도"})`}
                                sx={{ px: 2.25, py: 1 }}
                            />

                            <Box sx={{ flex: 1 }} />

                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary" }}>
                                선택된 시·군·구: {selectedSigungu.length.toLocaleString("ko-KR")}개
                            </Typography>
                        </Stack>

                        {/* 지역 선택 UI */}
                        <Paper variant="outlined" sx={{ ...ds.components.external.card, overflow: "hidden" }}>
                            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 2fr" } }}>
                                {/* 시·도 */}
                                <Box sx={{ borderRight: { xs: "none", md: `1px solid ${ds.colors.border}` } }}>
                                    <Box sx={{ p: 2, borderBottom: `1px solid ${ds.colors.border}` }}>
                                        <Typography sx={{ fontWeight: 800, fontSize: 14, letterSpacing: "-0.2px" }}>시·도</Typography>
                                        <Typography sx={{ mt: 0.25, fontWeight: 800, fontSize: 12, color: "text.secondary" }}>
                                            클릭해서 선택
                                        </Typography>
                                    </Box>

                                    <Box sx={{ maxHeight: 260, overflowY: "auto", p: 1.25 }}>
                                        {sidoKeys.map((sidoKey) => {
                                            const sidoObj = groupedRegions[sidoKey];
                                            const active = activeSido === sidoKey;
                                            const label = String(sidoKey).replace("특별시", "").replace("광역시", "").replace("도", "");

                                            return (
                                                <Box
                                                    key={sidoKey}
                                                    onClick={() => setActiveSido(sidoKey)}
                                                    sx={{
                                                        cursor: "pointer",
                                                        borderRadius: 3,
                                                        py: 1.1,
                                                        px: 1.25,
                                                        mb: 0.75,
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        alignItems: "center",
                                                        ...(active
                                                            ? { bgcolor: "rgba(0,0,0,0.06)" }
                                                            : { bgcolor: "transparent", "&:hover": { bgcolor: ds.colors.bg } }),
                                                    }}
                                                >
                                                    <Typography sx={{ fontWeight: active ? 950 : 850, fontSize: 13 }} noWrap>
                                                        {label}
                                                    </Typography>
                                                    <Typography sx={{ fontWeight: 600, fontSize: 12, color: "text.secondary" }}>
                                                        ({Number(sidoObj?.totalVenueCount || 0)})
                                                    </Typography>
                                                </Box>
                                            );
                                        })}
                                        {sidoKeys.length === 0 && !loadingRegions && (
                                            <Typography sx={{ p: 1, fontWeight: 800, color: "text.secondary" }}>지역 데이터가 없습니다.</Typography>
                                        )}
                                    </Box>
                                </Box>

                                {/* 시·군·구 */}
                                <Box>
                                    <Box
                                        sx={{
                                            p: 2,
                                            borderBottom: `1px solid ${ds.colors.border}`,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            gap: 1,
                                        }}
                                    >
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography sx={{ fontWeight: 800, fontSize: 14, letterSpacing: "-0.2px" }}>시·군·구</Typography>
                                            <Typography sx={{ mt: 0.25, fontWeight: 800, fontSize: 12, color: "text.secondary" }}>
                                                {activeSidoObj?.sigunguList?.length ? `${activeSidoObj.sigunguList.length}개` : "선택된 시·도가 없습니다"}
                                            </Typography>
                                        </Box>

                                        {/* ✅ 현재 시/도만 전체 선택/해제 */}
                                        <IconButton
                                            disabled={!activeSidoObj}
                                            onClick={toggleAllInActiveSido}
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: 1.5,
                                                bgcolor: ds.colors.primaryBg,
                                                border: `1px solid ${ds.colors.primaryBorder}`,
                                                color: ds.colors.primary,
                                                "&:hover": { bgcolor: ds.colors.primaryLight },
                                            }}
                                            title={isAllSelectedInActiveSido ? "전체 해제(현재 시/도)" : "전체 선택(현재 시/도)"}
                                        >
                                            {isAllSelectedInActiveSido ? <RemoveDoneRoundedIcon /> : <SelectAllRoundedIcon />}
                                        </IconButton>
                                    </Box>

                                    <Box sx={{ maxHeight: 260, overflowY: "auto", p: 1.5 }}>
                                        {activeSidoObj ? (
                                            <Box
                                                sx={{
                                                    display: "grid",
                                                    gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr", md: "1fr 1fr 1fr 1fr" },
                                                    gap: 1,
                                                }}
                                            >
                                                {activeSidoObj.sigunguList.map((item) => {
                                                    const key = `${item.sido}-${item.sigungu}`;
                                                    const checked = selectedSigungu.includes(key);

                                                    return (
                                                        <Chip
                                                            key={key}
                                                            label={String(item.sigungu)}
                                                            clickable
                                                            onClick={() => toggleSigungu(item.sido, item.sigungu)}
                                                            sx={{
                                                                borderRadius: 1.5,
                                                                height: 36,
                                                                justifyContent: "flex-start",
                                                                ...(checked
                                                                    ? {
                                                                        bgcolor: ds.colors.primaryBg,
                                                                        border: `1px solid ${ds.colors.primaryBorder}`,
                                                                        color: ds.colors.primary,
                                                                    }
                                                                    : {
                                                                        bgcolor: ds.colors.bg,
                                                                        border: `1px solid ${ds.colors.border}`,
                                                                        "&:hover": { bgcolor: ds.colors.borderLight },
                                                                    }),
                                                                "& .MuiChip-label": { fontWeight: checked ? 950 : 850, fontSize: 12 },
                                                            }}
                                                        />
                                                    );
                                                })}
                                            </Box>
                                        ) : (
                                            <Typography sx={{ fontWeight: 800, color: "text.secondary" }}>왼쪽에서 시·도를 선택해주세요.</Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        </Paper>

                        {/* 선택 요약 */}
                        {selectedChips.length > 0 && (
                            <Box>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", mb: 1 }}>
                                    선택된 시·군·구 ({selectedChips.length}개)
                                </Typography>
                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                    {selectedChips.slice(0, 30).map((c) => (
                                        <Chip
                                            key={c.key}
                                            label={c.label}
                                            onDelete={() => setSelectedSigungu((prev) => prev.filter((k) => k !== c.key))}
                                            sx={{
                                                ...ds.components.external.chip,
                                                fontWeight: 700,
                                            }}
                                        />
                                    ))}
                                    {selectedChips.length > 30 && (
                                        <Chip label={`+${selectedChips.length - 30}개 더`} sx={ds.components.external.chip} />
                                    )}
                                </Stack>
                            </Box>
                        )}
                    </Stack>
                </Paper>

                {/* 검색 결과 */}
                {searchTriggered && (
                    <Paper
                        variant="outlined"
                        sx={{
                            mt: 2.25,
                            ...ds.components.external.card,
                            overflow: "hidden",
                        }}
                    >
                        <Box sx={{ p: 2.25, borderBottom: `1px solid ${ds.colors.border}` }}>
                            <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ xs: "flex-start", md: "center" }}>
                                <Typography sx={{ fontSize: 15, fontWeight: 800, letterSpacing: "-0.2px" }}>검색 결과</Typography>
                                <Box sx={{ flex: 1 }} />
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary" }}>
                                    {visibleVenues.length.toLocaleString("ko-KR")}건
                                    <span style={{ opacity: 0.7 }}> (최소 {minCapacity.toLocaleString("ko-KR")}명 필터 적용)</span>
                                </Typography>
                            </Stack>
                        </Box>

                        {visibleVenues.length === 0 && !loadingVenues ? (
                            <Box sx={{ p: 3 }}>
                                <Typography sx={{ fontWeight: 700, color: "text.secondary" }}>
                                    결과가 없습니다. 조건을 바꿔서 다시 검색해보세요.
                                </Typography>
                            </Box>
                        ) : (
                            <Box sx={{ p: 2.25, overflowX: "auto" }}>
                                <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
                                    <Box component="thead">
                                        <Box component="tr" sx={{ borderBottom: `1px solid ${ds.colors.border}` }}>
                                            <Box component="th" sx={thSx}>주소</Box>
                                            <Box component="th" sx={thSx}>사업장명</Box>
                                            <Box component="th" sx={thSx}>세부공간</Box>
                                            <Box component="th" sx={thSx}>인원</Box>
                                            <Box component="th" sx={thSx}>특이사항</Box>
                                        </Box>
                                    </Box>

                                    <Box component="tbody">
                                        {visibleVenues.map((venue, idx) => (
                                            <Box
                                                component="tr"
                                                key={idx}
                                                sx={{
                                                    borderBottom: `1px solid ${ds.colors.borderLight}`,
                                                    "&:hover": { bgcolor: ds.colors.bg },
                                                }}
                                            >
                                                <Box component="td" sx={tdSx}>{venue.address}</Box>
                                                <Box component="td" sx={{ ...tdSx, fontWeight: 700 }}>{venue.businessName}</Box>
                                                <Box component="td" sx={tdSx}>{venue.detailSpace}</Box>
                                                <Box component="td" sx={{ ...tdSx, fontWeight: 700 }}>
                                                    {venue.minCapacity}-{venue.maxCapacity}명
                                                </Box>
                                                <Box component="td" sx={tdSx}>{venue.mergeAvailableRooms}</Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </Paper>
                )}
            </Container>

            {/* 로딩 */}
            <Backdrop
                open={loadingRegions || loadingVenues}
                sx={{ zIndex: (t) => t.zIndex.modal + 10, bgcolor: "rgba(255,255,255,0.85)" }}
            >
                <LoadingDots
                    label={loadingRegions ? "지역 목록을 불러오고 있어요" : "행사장을 검색하고 있어요"}
                />
            </Backdrop>

        </Box>
    );
}

const thSx = {
    textAlign: "left",
    padding: "10px 10px",
    fontSize: 12,
    fontWeight: 800,
    color: ds.colors.textSecondary,
    whiteSpace: "nowrap",
};

const tdSx = {
    textAlign: "left",
    padding: "10px 10px",
    fontSize: 12,
    fontWeight: 800,
    color: ds.colors.textPrimary,
    whiteSpace: "nowrap",
};
